import { MapPin } from "lucide-mui"
import React from "react"

import i18n from "@/i18n"
import { isVenueOpen, getTodayHours } from "@/utils/opening_hours"

import ShareButton from "./share_button"
import type { VenueHeaderProps } from "./types"

const STYLES = {
  container: "mb-4 pb-4",
  row: "flex items-start justify-between gap-4",
  content: "min-w-0 flex-1",
  title: "mt-0 text-[26px] font-medium leading-[26px] text-[#201f1f]",
  tagsContainer: "mt-4 flex flex-wrap gap-[6px]",
  tag: "rounded-[5px] bg-[#f1f1f0] px-2 py-[5px] text-[12px] leading-[12px] text-[#374151]",
  addressContainer: "mt-[15px] flex flex-wrap items-center gap-x-2 gap-y-1",
  mapPinIcon: "h-[14px] w-[14px] shrink-0 text-[#9ca3af]",
  addressText: "text-[14px] leading-4 text-[#201f1f]",
  mapLink: "text-[14px] font-medium leading-4 text-[#2596fa] no-underline hover:underline",
  statusContainer: "mt-[10px]",
  statusBadge: "inline-flex items-center gap-1 rounded-[5px] px-[6px] py-[2px] text-[12px] leading-4",
  middot: "font-semibold text-black",
} as const

const VenueHeaderDesktop: React.FC<VenueHeaderProps> = ({
  name,
  address,
  zipcode,
  city,
  openingHours,
  timezone,
  tags,
}) => {
  const isOpen = isVenueOpen(openingHours, timezone)
  const todayHours = getTodayHours(openingHours, timezone)

  const venueTypeTags = tags?.filter((t) => t.category === "venue_type") || []
  const cityLine = [zipcode, city].filter(Boolean).join(" ")
  const locationParts = [address, cityLine].filter(Boolean)
  const locationText = locationParts.join(", ")
  const canShowMap = Boolean(address && city)
  const mapQuery = [name, address, zipcode, city].filter(Boolean).join(", ")

  return (
    <div className={STYLES.container} data-testid="public-venue-header-container">
      <div className={STYLES.row}>
        <div className={STYLES.content}>
          <h1 className={STYLES.title} data-testid="public-venue-header-name">
            {name}
          </h1>

          {venueTypeTags.length > 0 && (
            <div className={STYLES.tagsContainer} data-testid="public-venue-header-tags">
              {venueTypeTags.map((tag) => (
                <span key={tag.value} className={STYLES.tag} data-testid={`public-venue-header-tag-${tag.value}`}>
                  {tag.label}
                </span>
              ))}
            </div>
          )}

          {!!locationText && (
            <div className={STYLES.addressContainer} data-testid="public-venue-header-address">
              <MapPin className={STYLES.mapPinIcon} />
              <span className={STYLES.addressText}>{locationText}</span>
              {!!canShowMap && (
                <>
                  <span className={STYLES.addressText}>-</span>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={STYLES.mapLink}
                    data-testid="public-venue-header-map-link"
                  >
                    {i18n.t("app.public.venue.show_map")}
                  </a>
                </>
              )}
            </div>
          )}

          <div className={STYLES.statusContainer} data-testid="public-venue-header-status">
            <span
              className={`${STYLES.statusBadge} ${
                isOpen ? "bg-[#edfff2] text-[#303239]" : "bg-[#fff1f1] text-[#303239]"
              }`}
            >
              <span
                className={isOpen ? "font-medium text-[#13b831]" : "font-medium text-[#dc2626]"}
                data-testid="public-venue-header-open-status"
              >
                {isOpen ? i18n.t("app.public.venue.status.open_now") : i18n.t("app.public.venue.status.closed")}
              </span>
              {!!isOpen && !!todayHours?.endTime && (
                <>
                  <span className={STYLES.middot}>&middot;</span>
                  <span data-testid="public-venue-header-hours">
                    {i18n.t("app.public.venue.status.closes_at", { time: todayHours.endTime })}
                  </span>
                </>
              )}
            </span>
          </div>
        </div>

        <ShareButton venueName={name} />
      </div>
    </div>
  )
}

export default VenueHeaderDesktop
